import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import ButtonComponent from '#app/Component/ButtonComponent';
import SelectComponent from '#app/Component/SelectComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import { Form, FormField, initFormHandler } from '#app/Handler/FormHandler';
import { Payload } from '#app/View/Sale/OrderClient/SaleOrderClient/components/DisputeModal';
import { OrderDocument } from '#app/typings/orderDocument';

type Props = {
  documents: OrderDocument[];
  onClose: () => void;
  loading: boolean;
  onSubmit: (payload: Payload) => void;
};

const DisputeProduct: React.FC<Props> = ({ documents, onClose, loading, onSubmit }) => {
  const [orderDocument, setOrderDocument] = useState<number | undefined>(
    documents.length === 1 ? documents[0].id : undefined,
  );
  const getProductsForm = (orderDocumentId: number) => ({
    ...documents
      .find((document) => document.id === orderDocumentId)
      ?.orderDocumentByProducts.filter(({ quantity }) => quantity > 0)
      .reduce((acc: Record<string, FormField>, { orderClientByProduct, quantity }) => {
        acc[orderClientByProduct.byProduct.toString()] = {
          name: orderClientByProduct.byProduct.toString(),
          label: 'Quantité',
          textHelper: `Saisissez la quantité de produit en litige (maximum : ${quantity})`,
          type: 'integer',
          options: { max: quantity },
        };
        return acc;
      }, {}),
  });

  const [form, setForm] = useState<Form>({
    reason: {
      name: 'reason',
      label: 'Raison',
      textHelper: 'Saisissez une raison.',
      type: 'text',
    },
    ...(documents.length > 1 && {
      orderDocument: {
        name: 'orderDocument',
        label: 'Facture',
        textHelper: 'Selectionnez la facture en litige.',
        type: 'integer',
        options: { validation: ['required'] },
      },
    }),
    ...(orderDocument && getProductsForm(orderDocument)),
  });

  const handlerForm = initFormHandler(form, setForm);

  useEffect(() => {
    handlerForm.start();
  }, []);

  useEffect(() => {
    if (orderDocument) {
      setForm({
        ...form,
        ...getProductsForm(orderDocument as number),
      });
    }
  }, [orderDocument]);

  const handleSubmit = (): void => {
    if (handlerForm.checkError() > 0) {
      return;
    }
    const data = handlerForm.getData();
    const products = Object.entries(data)
      .filter(([key]) => !isNaN(parseInt(key)))
      .map(([key, value]) => ({
        quantity: value ?? 0,
        byProduct: parseInt(key),
      }));

    if (!products.some((product) => product.quantity > 0)) {
      dispatch(SnackbarOpen({ text: 'Il faut au moins un produit', variant: 'error' }));
      return;
    }

    handlerForm.setFormLoading(true);
    onSubmit({
      reason: data.reason,
      orderDocument: orderDocument as number,
      products,
    });
  };

  return (
    <>
      {!!form.reason && (
        <Grid size={12}>
          <TextFieldComponent id="reason" handler={handlerForm} multiline />
        </Grid>
      )}
      {!!form.orderDocument && (
        <Grid size={12}>
          <SelectComponent
            id="orderDocument"
            options={documents.map((document) => ({
              value: document.id,
              label: document.reference,
            }))}
            handler={handlerForm}
            onChange={(value) => setOrderDocument(value as number)}
          />
        </Grid>
      )}
      {documents
        .find((document) => document.id === orderDocument)
        ?.orderDocumentByProducts.map(
          (orderDocumentByProduct) =>
            !!form[orderDocumentByProduct.orderClientByProduct.byProduct] && (
              <React.Fragment key={orderDocumentByProduct.orderClientByProduct.byProduct}>
                <Grid size={6}>
                  <p>{orderDocumentByProduct.productLabel}</p>
                </Grid>
                <Grid size={6}>
                  <TextFieldComponent
                    id={orderDocumentByProduct.orderClientByProduct.byProduct.toString()}
                    handler={handlerForm}
                  />
                </Grid>
              </React.Fragment>
            ),
        )}

      <Grid size={12} container spacing={2} justifyContent="space-between">
        <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} loading={loading} />
        <ButtonComponent label="Enregistrer" onClick={handleSubmit} loading={loading} />
      </Grid>
    </>
  );
};

export default DisputeProduct;
